// Arvoke AI assistant types

// The user's allow/deny decision on the write-tool call the thread is paused
// on (see ChatStreamToolConfirmationRequired below). Only the call's id travels
// back: the backend resumes from the paused turn it stored itself, so what
// executes is always exactly the call that was previewed.
export interface PendingToolDecision {
  tool_use_id: string
  decision: 'allow' | 'deny'
}

// What the user is looking at when they send a message (decision 16 in
// backend/docs/architecture/arvoke_ai/arvoke_ai_decisions.md). The backend
// re-resolves every id through the user's permissions; display strings are
// what the user's screen shows.
export interface PageContextFilter {
  column_id: string
  operator: string
  value?: string | null
}

export interface PageContextSort {
  column_id: string
  direction: 'asc' | 'desc'
}

export interface PageContextTable {
  table_id: string
  view_id?: string | null
  has_unsaved_view_changes?: boolean
  filter_logic?: 'and' | 'or' | 'mixed' | null
  filters?: PageContextFilter[]
  sorts?: PageContextSort[]
  group_column_ids?: string[]
  pinned_column_ids?: string[]
  visible_field_count?: number | null
  total_field_count?: number | null
  total_rows?: number | null
  search_text?: string | null
}

export interface PageContextGrid {
  title?: string | null
  view?: string | null
  filter_logic?: 'and' | 'or' | 'mixed' | null
  filters?: { column: string; operator: string; value: string | null }[]
  sorts?: { column: string; direction: 'asc' | 'desc' }[]
  groups?: string[]
  columns?: string[]
  row_count?: number | null
  search_text?: string | null
}

/** Field Settings and its unsaved draft (the backend names the table and a saved field). */
export interface PageContextFieldEditor {
  field_id?: string | null
  table_id?: string | null
  mode: 'create' | 'edit'
  field_name?: string | null
  tab?: string | null
  docked?: boolean
  unsaved_changes?: boolean
  formula?: string | null
  formula_status?: 'empty' | 'checking' | 'valid' | 'invalid' | 'unavailable' | null
  formula_problems?: string[]
}

export interface PageContext {
  path: string
  navigation?: string[]
  title?: string | null
  breadcrumbs?: string[]
  active_tab?: string | null
  statuses?: string[]
  table?: PageContextTable | null
  grid?: PageContextGrid | null
  field_editor?: PageContextFieldEditor | null
  /** Messages the screen shows: a save bar's problems, a warning bar, an error toast. */
  notices?: string[]
}

// The backend stores every turn and builds the model's context from the
// thread (decision 18), so the client never replays history.
export interface ChatRequest {
  /** Omitted to start a new thread. */
  thread_id?: string
  message: string
  /** Edit/regenerate: drop this stored user message and everything after it,
   * then ask `message` in its place. */
  replace_from_message_id?: string
  pending_tool_decision?: PendingToolDecision
  page_context?: PageContext | null
  /** An incognito chat (decision 20): nothing is saved, and there's no
   * thread_id. Its earlier turns come back as `incognito_state`, the sealed
   * token the previous response ended with; omitted to start one. */
  incognito?: boolean
  incognito_state?: string
  /** Files uploaded first (`uploadChatAttachment`) that this question
   * carries (decision 23); with any, `message` may be blank. Never in an
   * incognito chat. */
  attachment_ids?: string[]
}

/** A file attached to a chat question (decision 23). `url` and
 * `thumbnail_url` (images) are links that last five minutes; a file card
 * asks for fresh ones when it's opened. `available` is false when the file
 * is gone or kept where this environment can't read it (a PR environment's
 * copy of production's chats); it then has no links. */
export interface ChatAttachment {
  attachment_id: string
  kind: 'image' | 'pdf' | 'text'
  file_name: string
  media_type: string
  size_bytes: number
  page_count?: number | null
  width?: number | null
  height?: number | null
  /** A text file longer than the AI reads: it reads the first part. */
  truncated?: boolean
  available: boolean
  url?: string | null
  thumbnail_url?: string | null
}

/** What the composer may attach here (`GET /assistant/attachments/limits`). */
export interface ChatAttachmentLimits {
  enabled: boolean
  max_files_per_message: number
  max_message_bytes: number
  image_max_bytes: number
  pdf_max_bytes: number
  pdf_max_pages: number
  text_max_bytes: number
}

// First event of every chat stream: the thread the turn was stored in (newly
// created for a request without thread_id) and the stored id of this turn's
// user message (null when resuming a paused write call). Resuming an allowed
// call, `tool_outcome` says how the call ended, before the model narrates it.
// An incognito chat has no thread: its thread_id is null.
export interface ChatStreamThread {
  type: 'thread'
  thread_id: string | null
  user_message_id: string | null
  tool_outcome?: 'done' | 'failed' | null
  created_column?: { table_id: string; column_id: string } | null
}

// Sent after a new thread's last answer event when the AI-written title
// replaced the one taken from its first question.
export interface ChatStreamThreadTitle {
  type: 'thread_title'
  thread_id: string
  title: string
}

export interface ChatStreamDelta {
  type: 'delta'
  text: string
}

// Answer feedback (decision 19): every finished request records an answer,
// what it offered, and every signal about it. Mirrors backend `schema.py`.
export type AnswerDownReason =
  | 'wrong_numbers'
  | 'wrong_records'
  | 'didnt_answer'
  | 'wrong_about_arvoke'
  | 'other'
export const ANSWER_COMMENT_MAX_LENGTH = 1000

/** A finished answer, for its feedback buttons. */
export interface ChatAnswer {
  answer_id: string
  /** `pinned`: the assistant was unsure of it, so the buttons stay on screen
   * with "Did this answer it?". `none`: no buttons (an action card). */
  feedback_prompt: 'none' | 'hover' | 'pinned'
  seen: boolean
  vote: 'up' | 'down' | null
  reason: AnswerDownReason | null
  comment: string | null
}

export interface AnswerVoteRequest {
  /** Null clears the vote. */
  vote: 'up' | 'down' | null
  /** Only kept with a thumbs down. */
  reason?: AnswerDownReason | null
  comment?: string | null
}

export interface AnswerFeedback {
  answer_id: string
  vote: 'up' | 'down' | null
  reason: AnswerDownReason | null
  comment: string | null
}

export interface AnswerUseRequest {
  kind: 'copied' | 'link_followed'
  /** The followed link's href, as the answer wrote it. */
  href?: string
}

// A web source the answer cited (decision 21). Anthropic's terms require
// these to be shown with the answer; they are rebuilt from the stored turns
// when a thread is reopened. `title` is null when the source had none.
export interface ChatCitation {
  url: string
  title: string | null
}

// A web search started: show "Searching the web..." until the next text.
export interface ChatStreamWebSearch {
  type: 'web_search'
}

export interface ChatStreamDone {
  type: 'done'
  model: string
  /** Every web source this turn's answer cited, deduplicated by URL. */
  citations?: ChatCitation[]
  /** Null when the answer couldn't be recorded. */
  answer?: ChatAnswer | null
}

export interface ChatStreamError {
  type: 'error'
  code: string
  message: string
}

// Sent instead of executing a write-tool call. `preview` is rendered
// server-side from `tool_input`, never from the model's text.
export interface ChatStreamToolConfirmationRequired {
  type: 'tool_confirmation_required'
  tool_use_id: string
  tool_name: string
  tool_input: Record<string, unknown>
  preview: string
  preceding_text?: string
  /** Web sources cited by this turn's text so far. */
  citations?: ChatCitation[]
}

// Emitted when a round ends in read-tool calls, just before they run: the
// text streamed since the previous one was the model working toward its
// answer ("Let me check the Projects table"), not the answer itself.
export interface ChatStreamToolCall {
  type: 'tool_call'
  tool_names: string[]
}

export interface RecordInterpretation {
  note_id: string
  revision: number
  thread_id: string | null
  summary: string
  kind: 'placeholder_entity' | 'entity_status' | 'sentinel_value'
  applies_when: string
  evidence: string
  confidence: 'provisional' | 'confirmed' | 'corroborated' | 'disputed' | 'needs_review'
  confirmation_count: number
  needs_confirmation: boolean
  feedback: 'confirm' | 'reject' | null
  available: boolean
}

export interface ChatStreamInterpretation {
  type: 'record_interpretation'
  interpretation: RecordInterpretation
}

// Exports from the chat (backend decision 22): an export the assistant
// queued, which the chat shows and watches, and an email it drafted, which
// opens in the Send dialog for the person to send. It never sends.
export interface ChatExport {
  export_id: string
  table_id: string | null
  file_name: string
  format: 'csv' | 'xlsx' | 'pdf'
  /** Opens the print dialog when it's ready, instead of downloading. */
  print: boolean
}

export interface ChatEmailDraft {
  exports: ChatExport[]
  to: string[]
  cc: string[]
  subject: string | null
  message: string | null
  from_mailbox: string | null
  /** Recipients whose domain no one in the organization has. */
  outside_recipients: string[]
}

export interface ChatStreamExport {
  type: 'export'
  export: ChatExport
}

export interface ChatStreamEmailDraft {
  type: 'email_draft'
  draft: ChatEmailDraft
}

// An incognito chat's turns so far, sealed by the server: sent first and last
// in each of its streams. The page keeps only the latest, in memory, and sends
// it with the next request.
export interface ChatStreamIncognitoState {
  type: 'incognito_state'
  state: string
}

export type ChatStreamEvent =
  | ChatStreamThread
  | ChatStreamThreadTitle
  | ChatStreamIncognitoState
  | ChatStreamDelta
  | ChatStreamWebSearch
  | ChatStreamDone
  | ChatStreamError
  | ChatStreamToolCall
  | ChatStreamToolConfirmationRequired
  | ChatStreamInterpretation
  | ChatStreamExport
  | ChatStreamEmailDraft

// Custom skills: per-user standing guidance the assistant applies when relevant.
export const CUSTOM_SKILL_TITLE_MAX_LENGTH = 200
export const CUSTOM_SKILL_CONTENT_MAX_LENGTH = 2000

export interface CustomSkill {
  custom_skill_id: string
  title: string
  content: string
  created_at: string
  updated_at: string
}

export interface CustomSkillsResponse {
  custom_skills: CustomSkill[]
}

export interface CreateCustomSkillRequest {
  title: string
  content: string
}

// Natural-language formula generation; the returned formula is already validated.
export interface GenerateFormulaRequest {
  table_id: string
  description: string
  /** Client-generated, stable for one prompt-panel session; each generation
   * logs a feedback row under it. */
  session_id: string
  /** The derived column being edited; absent for a new one. */
  column_id?: string | null
  current_formula?: string | null
  /** The field's name as the user has it now, saved or not, for the AI's context. */
  field_name?: string | null
}

export interface GenerateFormulaResponse {
  formula: string
  /** Pass to the flag endpoint to mark this generation as bad. */
  feedback_id: string
}

export interface FixFormulaRequest {
  table_id: string
  formula: string
  /** What the editor knows is wrong, in its words, beyond static validation. */
  problems?: string[]
  /** Fixes the editor worked out itself, tried before any AI. */
  candidates?: string[]
  /** The record the editor previews on: a fix must evaluate there too. */
  record_id?: string | null
  use_ai?: boolean
  /** The field's name as the user has it now, saved or not, for the AI's context. */
  field_name?: string | null
}

export interface FixFormulaResponse {
  /** A corrected formula that passed validation, or null (with `reason` when there is one). */
  formula: string | null
  source: 'rule' | 'ai' | null
  reason: string | null
  /** Why there's no fix, when the editor shows something for it. */
  code?: 'not_enough_information' | null
}

export interface FlagFormulaFeedbackResponse {
  ok: boolean
}

// Chat history (decision 18): per-user threads stored server-side. Strictly
// private: every endpoint is scoped to the caller, and another user's thread
// id is a 404. Mirrors backend `app/arvoke_ai/assistant/schema.py`.
export const CHAT_THREAD_TITLE_MAX_LENGTH = 200

export interface ChatThreadSummary {
  thread_id: string
  title: string
  created_at: string
  updated_at: string
  /** The caller's folder the thread is filed in; null (or absent) when unfiled. */
  folder_id?: string | null
}

export interface ChatThreadsResponse {
  /** Most recently active first. */
  threads: ChatThreadSummary[]
}

export interface ChatThreadToolConfirmation {
  tool_use_id: string
  tool_name: string
  tool_input: Record<string, unknown>
  preview: string
  /** Null while the thread is still paused on this call. */
  decision: 'allow' | 'deny' | 'superseded' | null
  /** For an allowed call: `done` when it succeeded, `failed` when it ran but
   * didn't succeed, `unconfirmed` when the result was never recorded. */
  outcome: 'done' | 'failed' | 'unconfirmed' | null
}

/** One chat bubble, already folded from the stored model turns. */
export interface ChatThreadMessage {
  message_id: string
  role: 'user' | 'assistant'
  /** For the assistant, the reply itself: the text after its last tool call. */
  content: string
  /** Working notes: the text of each round that went on to read tools. */
  reasoning?: string[]
  created_at: string
  tool_confirmation: ChatThreadToolConfirmation | null
  /** Web sources the bubble's text cites; empty when it cites none. */
  citations: ChatCitation[]
  record_interpretations?: RecordInterpretation[]
  /** Exports and email drafts its tool calls made. */
  exports?: ChatExport[]
  email_drafts?: ChatEmailDraft[]
  /** The answer this assistant bubble ended, when it was recorded. */
  answer?: ChatAnswer | null
  /** A user bubble's files. */
  attachments?: ChatAttachment[]
}

export interface ChatThreadDetail extends ChatThreadSummary {
  messages: ChatThreadMessage[]
}

export interface RenameChatThreadRequest {
  title: string
}

// Chat folders: the caller's own folders for their threads, private exactly
// as threads are. Deleting a folder unfiles its threads; it never deletes them.
export const CHAT_FOLDER_NAME_MAX_LENGTH = 100

export interface ChatFolderSummary {
  folder_id: string
  name: string
  created_at: string
  updated_at: string
}

export interface ChatFoldersResponse {
  /** By name, ignoring case. */
  folders: ChatFolderSummary[]
}

export interface ChatFolderNameRequest {
  name: string
}

export interface MoveChatThreadRequest {
  /** Null takes the thread out of its folder. */
  folder_id: string | null
}

export interface ChatThreadSearchResult {
  thread_id: string
  title: string
  updated_at: string
  /** Excerpt of the best-matching message; null when the title matched. */
  snippet: string | null
}

export interface ChatThreadSearchResponse {
  results: ChatThreadSearchResult[]
}
